import { useLayoutEffect, useState, type ReactNode, type ClipboardEvent, type FormEvent, type KeyboardEvent, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useLocation } from 'react-router';
import {
  ArrowUpIcon,
  BrainIcon,
  CircleNotchIcon,
  FilesIcon,
  ImageIcon,
  LightningIcon,
  MicrophoneIcon,
  PaperclipIcon,
  PhoneIcon,
  StopIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { FilePicker, type FilePickerOption } from '~/components/file-picker';
import { pickedFileAttachment } from './picked-file-attachment';
import { useVoiceStt } from '~/hooks/use-voice-stt';
import { useSttModel } from '~/hooks/queries/use-stt-model';
import { useFeatureFlag, IMAGE_GENERATION_FLAG, MODEL_PICKER_COMPOSER_FLAG } from '~/hooks/queries/use-feature-flags';
import { ComposerModelPicker } from './composer-model-picker';
import { RegionBadge } from '~/components/model-badges';
import { useSlashMenu } from './slash-menu';
import { useVoiceCall, type VoiceTurn } from '~/features/voice/use-voice-call';
import { VoiceCallBar } from '~/features/voice/voice-call-bar';
import { useHandsFreeCall } from '~/features/voice/hands-free/hands-free-call';
import { useRefreshHistoryWhenBackgroundSettles } from '~/features/voice/live-history-refresh';
import { useUploadFile, type FileMeta } from '~/hooks/queries/use-files';
import { BudgetComposerLock, useBudgetBlocked } from '~/features/limits/budget-blocked-card';
import { toast } from 'sonner';
import { decideQueuedSend, decideQueuedRestore, decideEnterAction } from './queued-send';
import { loadQueuedDraft, persistQueuedDraft, queuedDraftKey } from './queued-draft-storage';
import { useDraftText } from './use-draft-text';
import { clipboardImageFiles } from '~/lib/clipboard';
import type { ChatModelMode } from '~/lib/chat-model-mode';
import {
  useChatModelMode,
  usePickChatModelMode,
  useDefaultChatModelMode,
  useSetDefaultChatModelMode,
} from '~/hooks/queries/use-chat-model-mode';
import { registerDesktopComposer, desktopComposerFiles } from './desktop-composer-bridge';
import { Tip } from '~/components/ui/tip';

type PendingAttach =
  | { state: 'uploading'; localId: string; name: string }
  | {
      state: 'ready';
      localId: string;
      fileId: string;
      name: string;
      contentType: string;
      kind: FileMeta['kind'];
      /** FRONT-1567: "upload" for a freshly-uploaded/device file (the only
       *  kind before this ticket), "document" for a picked Knowledge
       *  document — see `ChatAttachment`'s docblock for the wire meaning. */
      source: 'upload' | 'document';
    };

/**
 * FRONT-1567: `source` is optional on the wire — absent means "upload" (the
 * whole shape before this ticket) so every existing caller (the phone
 * composer, the inbox modal's `contextAttachments`, a queued draft) keeps
 * working untouched. When `source === 'document'`, `fileId` carries a saved
 * document's FILENAME rather than an upload id — see
 * `src/agents/chat-attachments.ts`'s `AttachmentRef` docblock for the
 * server-side mirror of this contract.
 */
export interface ChatAttachment {
  fileId: string;
  name: string;
  contentType: string;
  kind: string;
  source?: 'upload' | 'document';
}

/** FRONT-761: a snapshot of the composer, held for auto-send after the turn. */
export interface QueuedDraft {
  text: string;
  attachments: ChatAttachment[];
  image?: { intent?: boolean; model?: string };
}

// Merge a fresh snapshot into an existing queued draft (Enter while queued
// APPENDS — the next turn gets all stacked thoughts as one message), or start
// the queue with it.
/** FRONT-2555: takes one answered text back out of the queue — the whole
 *  queue when it was the only thing in it, otherwise just that part. */
function withoutQueuedText(prev: QueuedDraft | null, text: string): QueuedDraft | null {
  if (!prev) return prev;
  if (prev.text === text) return prev.attachments.length === 0 && !prev.image ? null : { ...prev, text: '' };
  const parts = prev.text.split('\n\n');
  const at = parts.indexOf(text);
  if (at < 0) return prev;
  parts.splice(at, 1);
  return { ...prev, text: parts.join('\n\n') };
}

function mergeQueuedDraft(prev: QueuedDraft | null, snap: QueuedDraft): QueuedDraft {
  if (!prev) return snap;
  return {
    text: `${prev.text}\n\n${snap.text}`,
    attachments: [...prev.attachments, ...snap.attachments],
    image: prev.image ?? snap.image,
  };
}

interface ChatInputProps {
  desktopComposer?: boolean;
  onSend?: () => void;
  /**
   * FRONT-1898: may return a promise. When it rejects, `submitPayload`
   * restores the snapshot into the box instead of losing it — the surface
   * is still responsible for its own user-facing error message (e.g. a
   * toast) and must rethrow after showing it, or this component never
   * learns the send failed. A surface whose `onSubmit` never rejects
   * (returns void, or a promise that never rejects) sees no behavior
   * change at all.
   */
  onSubmit: (
    text: string,
    attachments: ChatAttachment[],
    image?: { intent?: boolean; model?: string },
    /** FRONT-2555: `queued` when the queue fired this send on its own. */
    meta?: { queued?: boolean; desktopSignal?: AbortSignal },
  ) => void | Promise<unknown>;
  onStop: () => void;
  isBusy: boolean;
  isStreaming?: boolean;
  /** Active chat branch — voice turns get persisted with this branch id. */
  activeBranchId?: string;
  /**
   * Called by the voice-call hook after a call ends AND at least one turn was
   * exchanged. Receives the captured transcript so the parent (chat.tsx) can
   * hand it to the end-of-call endpoint. Forwarded straight to useVoiceCall.
   */
  onCallEnded?: (turns: VoiceTurn[]) => void;
  /**
   * Hide the one-shot mic dictation affordances (mic/STT button + STT model
   * picker). Kept separate from `hideLiveCall` (FRONT-359) so dictation can be
   * enabled on a surface while the live voice call stays off. With the start
   * button gone, the recording bar never activates.
   */
  hideDictation?: boolean;
  /**
   * Hide the live voice-call (phone) button. Surfaces that aren't ready for
   * the hands-free realtime call yet (the coding window) pass this while still
   * allowing dictation. With the button gone, the call bar never activates.
   * FRONT-531: the chat surfaces (home launcher, zen modal) no longer pass it.
   */
  hideLiveCall?: boolean;
  /**
   * FRONT-531: bind the live voice call to this chat session instead of the
   * global canonical one. The zen modal passes its own `web:<uuid>` thread so
   * the call's turns + recap + save proposal land in that thread.
   */
  voiceSessionId?: string;
  /**
   * FRONT-1336: the Work container this chat is pinned to. Forwarded to the
   * voice session mint so a call started before the chat's first typed turn
   * still knows which project it is on.
   */
  voiceScopeEntityId?: string;
  /**
   * FRONT-1841: the Work container this chat is pinned to, threaded the same
   * way as `voiceScopeEntityId` above — a SEPARATE prop (not a reuse of that
   * one) so a future divergence between the voice-call scope and the
   * upload-attach scope can't silently couple the two. Passed on every
   * upload (drop, paste, or the file-picker button — all funnel through
   * `handleFiles`) so the backend can auto-file it onto this container, the
   * same attachment `file_write` already produces for what Eddy writes.
   */
  uploadScopeEntityId?: string;
  /**
   * FRONT-570: the content of the file/document this chat is anchored to. The
   * zen modal passes the asset it's open on so a voice call launched here is
   * seeded with that asset's content (the chat history is loaded server-side).
   */
  voiceAssetContext?: string;
  /**
   * FRONT-531: intercept the phone button instead of starting a call in place.
   * The home launcher uses this to mint a fresh chat thread and open it with
   * the call already running (a voice call is just a chat that starts by
   * talking) — the launcher has no thread of its own to host the call.
   */
  onStartCall?: () => void;
  /**
   * FRONT-531: start the live voice call once on mount. Set by the zen modal
   * when it was opened from the launcher's phone button, so the user goes
   * straight into the call without a second tap.
   */
  autoStartCall?: boolean;
  /**
   * FRONT-254: this composer's window can hand its call to the hands-free
   * call at the app shell (only takes effect with `eddy.voice-hands-free` on).
   * The call then outlives the window: `onMinimize` closes the window and the
   * strip at the top takes over. `title` names the chat on that strip.
   */
  handsFreeCall?: { title: string | null; onMinimize: () => void };
  /** Custom idle placeholder. Defaults to "Ask {Eddy} anything…". */
  placeholder?: string;
  /** Submit-button label. Defaults to "Send"; the launcher passes "Start". */
  submitLabel?: string;
  /**
   * Hide the image-generation button + model picker (FRONT-535). Surfaces that
   * aren't image-gen contexts (the coding window, the new-chat launcher) pass
   * this; the main chat composer leaves it off so the button shows.
   */
  hideImageGen?: boolean;
  /**
   * FRONT-2522: text-only composer — hides the paperclip menu AND turns the
   * drop / paste upload paths into no-ops (`handleFiles`). Set for a direct
   * agent chat, whose runner sends text only; a hidden button with a live
   * drop zone would still let a file ride along and be silently ignored.
   */
  hideAttach?: boolean;
  /**
   * FRONT-576: hide the "Fast" / "Thoughtful" quick-switch buttons entirely.
   * Only for surfaces whose send path doesn't resolve the orchestrator model
   * at all (the coding window, which runs the Agent SDK) — every other
   * surface should leave this off and show the buttons.
   */
  hideModelSwitch?: boolean;
  /**
   * FRONT-2522: never open the slash-command menu. A direct agent chat
   * bypasses Eddy's command dispatcher, so "/note" there is plain text for the
   * agent — a menu offering Eddy's skills would promise what cannot happen.
   */
  hideSlashMenu?: boolean;
  /**
   * FRONT-820 / FRONT-1035 / FRONT-1455: the conversation this composer
   * belongs to. When set, the Fast/Thoughtful mode is scoped to (and
   * persisted under) this session id — AND every pick also mirrors into the
   * user's standing default in the same click (`usePickChatModelMode`), so
   * it's no longer scoped-and-persisted-here-only. When absent — the home
   * launcher's pre-session composer, before any conversation exists — the
   * buttons instead read/write ONLY the user's standing default mode, which
   * seeds the mode of whatever chat gets started next.
   */
  sessionId?: string;
  /**
   * FRONT-1036: the key the draft text is persisted under. Defaults to
   * `sessionId` (the common case — one draft per conversation). Surfaces
   * with no `sessionId` yet, like the home launcher's pre-session composer,
   * pass a fixed literal instead so their draft still survives navigation —
   * that's a separate concern from `sessionId`'s effect on model-mode
   * (passing a fake `sessionId` there would wrongly disable the standing
   * default-mode lookup FRONT-1035 relies on).
   */
  draftKey?: string;
  /**
   * FRONT-761: true when the just-ended turn failed. A queued mid-stream
   * draft must never auto-send into a broken state — with this set, the
   * queue holds (chip stays) instead of firing when the turn ends. Surfaces
   * that don't pass it get fire-on-end for every non-stopped completion.
   */
  turnError?: boolean;
  /**
   * FRONT-761: the turn-in-flight signal the queued send waits on; defaults
   * to isBusy. A local stream can DROP mid-turn while the server keeps
   * writing (FRONT-204 background continuation, FRONT-638 resume) — a
   * busy -> idle flip is then NOT completion, and firing into the resume
   * window splices the queued bubble into the middle of the running turn.
   * The inbox modal passes `isStreaming || run-still-running` (2s poll) so
   * the queue fires only when the turn has genuinely settled.
   */
  queueBusy?: boolean;
  /**
   * FRONT-1163: true while the caller doesn't yet know the real turn status
   * for this session (the inbox modal's bgRun poll hasn't answered its first
   * fetch). Gates ONLY the one-time reconciliation of a queued draft restored
   * from localStorage after a remount — everywhere else queueBusy/isBusy
   * already have a safe default. Omit when the surface has no such
   * async status source (its restored-draft check just resolves immediately).
   */
  queuePending?: boolean;
  /**
   * FRONT-1182: true when a submit from THIS composer would settle the very
   * thing currently making the turn active — the coding session sets it while
   * a run is genuinely parked on an open question card, because its own
   * `onSubmit` routes free text into that card as the answer.
   *
   * Without it, Enter defers to the queue on any active turn, and the queue
   * only resolves on a busy -> idle edge that nothing but this submit can
   * produce. The draft then waits forever on itself. Omit on every surface
   * whose composer cannot unblock its own turn; the queueing behaviour is
   * unchanged there.
   */
  submitResolvesTurn?: boolean;
  /**
   * FRONT-2555: asked first when Enter would queue a message mid-turn. If it
   * resolves true, the text answered the approval card the turn is waiting
   * on and is not queued; false queues it exactly as before. Only plain text
   * is offered (a message with attachments always queues).
   */
  onAnswerWhileBusy?: (text: string) => Promise<boolean>;
  /**
   * FRONT-3155: content rendered INSIDE the composer frame, above the text
   * field — the Builder's answer panel. A function so it can read what is
   * typed (the panel's reason line depends on it) and move focus to Send.
   */
  topSlot?: (state: { text: string; focusSend: () => void; send: () => void }) => ReactNode;
  /**
   * FRONT-3155: replaces the default "Send needs text" rule while set. The
   * Builder's panel can make Send valid with NO typed text (answers chosen),
   * or invalid WITH text (a batch that cannot be answered yet) — so the host
   * decides, from the typed text, and the button, Enter and the submit path
   * all read this one answer.
   */
  sendGate?: (text: string) => { disabled: boolean };
}

/**
 * 2026-07-21 (FRONT-907): lets an ancestor outside this component start the
 * SAME internal voice call — the day-one interview offer's "Let's talk"
 * quick-reply lives in the message list, not the composer, so it can't reach
 * `voiceCall.start` any other way. Callers that need to redirect the call
 * elsewhere still use the existing `onStartCall` prop.
 *
 * FRONT-3138: `addFiles` lets a surface take a file drop on a larger area
 * than the composer (the Build window takes it anywhere on the window) and
 * hand it to the composer's own upload path — same staging, same chips, same
 * `hideAttach` no-op as a drop on the box itself.
 */
export interface ChatInputHandle {
  startCall: () => void;
  addFiles: (files: File[]) => void;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput({ desktopComposer, onSend, onSubmit, onStop, isBusy, isStreaming, activeBranchId, onCallEnded, hideDictation: hideDictationProp, hideLiveCall: hideLiveCallProp, voiceSessionId, voiceAssetContext, voiceScopeEntityId, uploadScopeEntityId, onStartCall, autoStartCall, handsFreeCall, placeholder, submitLabel, hideImageGen: hideImageGenProp, hideAttach: hideAttachProp, hideModelSwitch: hideModelSwitchProp, hideSlashMenu: hideSlashMenuProp, sessionId, draftKey, turnError, queueBusy, queuePending, submitResolvesTurn, onAnswerWhileBusy, topSlot, sendGate }: ChatInputProps, ref) {
  const hideDictation = hideDictationProp;
  const hideLiveCall = hideLiveCallProp;
  const hideImageGen = hideImageGenProp;
  const hideAttach = hideAttachProp;
  const hideModelSwitch = hideModelSwitchProp;
  const hideSlashMenu = hideSlashMenuProp;
  const [text, setText] = useDraftText(draftKey ?? sessionId);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  // FRONT-1182: the queued draft is scoped exactly like the text draft above.
  // These two used to disagree, which left any draftKey-only surface unable to
  // persist a queued follow-up at all.
  const queuedKey = queuedDraftKey(sessionId, draftKey);
  // FRONT-761: Enter while a turn runs SNAPSHOTS the draft into this queue
  // slot and clears the box — like a real send — so the composer stays free
  // for the next thought. (The first cut kept the queued text in the box and
  // fired the box content, which stole whatever the user was typing when the
  // queue resolved.) Enter again while queued appends to the snapshot. On
  // Stop/error the snapshot is restored into the composer, never dropped.
  // FRONT-1163: hydrated from localStorage on mount — every chat window
  // remounts fresh per chat, so this is what survives switching away and back.
  const [queuedDraft, setQueuedDraft] = useState<QueuedDraft | null>(() => loadQueuedDraft(queuedKey));
  const queued = queuedDraft !== null;
  const stopRequestedRef = useRef(false);
  // The signal the queue resolves on: prefers the surface's settled-turn
  // signal (queueBusy) over the local stream flag, so a mid-turn stream drop
  // can't masquerade as completion. (FRONT-761)
  const queueTurnActive = queueBusy ?? isBusy;
  const prevBusyRef = useRef(queueTurnActive);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  function focusInput() {
    inputRef.current?.focus();
  }
  const fileInputRef = useRef<HTMLInputElement>(null);
  // FRONT-1567 review fix (round 2): ONE shared deferral for both attach-menu
  // items ("Upload from device" and "Choose from Knowledge") — set by an
  // item's onSelect, read (and cleared) by the attach menu's
  // onCloseAutoFocus once the menu has genuinely finished closing. Two
  // separate refs/timings here is exactly the "divergent hacks" this was
  // consolidated away from — see that handler's comment for the mechanism
  // both items are protected against.
  const pendingAttachActionRef = useRef<'upload' | 'browse' | null>(null);
  const [attachments, setAttachments] = useState<PendingAttach[]>([]);
  const taggedFiles = attachments;
  // FRONT-1567: the "choose from Knowledge" picker, opened from the
  // paperclip's dropdown menu rather than the picker's own uncontrolled
  // trigger — see the render below for why it's mounted outside the menu.
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const upload = useUploadFile();
  const location = useLocation();
  const displayName = 'EDDY';
  // FRONT-1194: false unless the workspace enforces compute budgets AND this
  // person's is spent.
  const budgetBlocked = useBudgetBlocked();

  useEffect(() => {
    const draft = (location.state as { draft?: string } | null)?.draft;
    if (draft) {
      setText((prev) => (prev ? prev : draft));
      window.history.replaceState({}, '');
      requestAnimationFrame(() => focusInput());
    }
  }, [location.state]);

  const [recordingMs, setRecordingMs] = useState(0);

  // FRONT-1034: the STT model is a persisted per-user setting (Settings →
  // Models) instead of a localStorage-only composer picker, shared with
  // every other dictation surface via useSttModel.
  const sttModel = useSttModel();

  // FRONT-535: image generation. `imageMode` primes the next message to call
  // generate_image (one-shot — reset after send). FRONT-822: no model picker
  // — GPT Image 2 is the sole, backend-hardcoded default (it isn't
  // discoverable via the proxy's model catalogue, so a catalog-driven picker
  // can't list it anyway).
  const [imageMode, setImageMode] = useState(false);
  // Gate on the org's image-generation flag (FRONT-535). Flag OFF hides the
  // button and the send-time intent — matching "today's behaviour" rather
  // than showing a button that errors. Fail-open while flags load so a slow
  // endpoint doesn't hide a working feature (the server still gates execution).
  // FRONT-576/FRONT-820/FRONT-1035/FRONT-1455: the quick-switch chat mode
  // ("Fast" / "Thoughtful"). Sticky — stays active until toggled off. With a
  // `sessionId` it's scoped to that conversation (chat_sessions.model_mode)
  // AND mirrored into the user's standing default in the same click
  // (`usePickChatModelMode`) — without one (the classic pre-session
  // composer) there's no conversation yet, so it reads/writes only the
  // standing default, which seeds new chats going forward. Both hooks are
  // always called (never conditionally, per the rules of hooks) — whichever
  // pair actually has a live query/mutation depends on `sessionId`.
  const sessionMode = useChatModelMode(sessionId);
  const defaultMode = useDefaultChatModelMode(!sessionId);
  const setSessionMode = usePickChatModelMode(sessionId);
  const setDefaultMode = useSetDefaultChatModelMode();
  const modelMode = sessionId ? sessionMode.data : defaultMode.data;
  const modelModePending = sessionId ? setSessionMode.isPending : setDefaultMode.isPending;
  const pickModelMode = (mode: ChatModelMode) => {
    // Tapping the active button turns it off (back to your normal chat model).
    const next = modelMode === mode ? null : mode;
    if (sessionId) {
      setSessionMode.mutate(next);
    } else {
      setDefaultMode.mutate(next);
    }
  };

  // FRONT-1698: flag on → the pills below are replaced by the model picker.
  const modelPickerEnabled = useFeatureFlag(MODEL_PICKER_COMPOSER_FLAG, false);
  const imageGenEnabled = useFeatureFlag(IMAGE_GENERATION_FLAG, true);
  const hasImageModels = !hideImageGen && imageGenEnabled;
  // Image intent is only meaningful when generation is actually available —
  // derive it once so a stale `imageMode` (flag vanished mid-session) can't
  // prime an image turn.
  const imageModeActive = hasImageModels && imageMode;

  const voice = useVoiceStt({
    model: sttModel || undefined,
    onTranscript: (transcript) => {
      setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
      requestAnimationFrame(() => focusInput());
    },
  });

  // Live voice-call (OpenAI Realtime). Independent from the one-shot mic
  // STT above. Shows the running user + assistant transcripts in the bar
  // while live; the persisted Postgres rows are pulled into the chat log
  // on call end via /history invalidation.
  const [callUserTranscript, setCallUserTranscript] = useState('');
  const [callAssistantStream, setCallAssistantStream] = useState('');
  const voiceCall = useVoiceCall({
    branchId: activeBranchId ?? 'main',
    sessionId: voiceSessionId,
    assetContext: voiceAssetContext,
    scopeEntityId: voiceScopeEntityId,
    onUserTranscript: (text) => setCallUserTranscript(text),
    onAssistantDelta: (delta) => setCallAssistantStream((prev) => prev + delta),
    onAssistantFinal: () => setCallAssistantStream(''),
    onCallEnded,
  });
  // FRONT-254: with the flag on, a call started here belongs to the app
  // shell (`HandsFreeCallProvider`) so it survives this window closing; this
  // composer only shows and steers it while the call is for THIS chat. Flag
  // off (or no provider, or a surface that doesn't opt in): the composer's
  // own call above, exactly as before.
  const hf = useHandsFreeCall();
  // Starting a hands-free call needs the flag; steering one that is already
  // up does not — a failed flags refetch must never strand a live call.
  const hfUsable = hf ? hf.enabled || hf.active : false;
  const handsFree = !!handsFreeCall && hfUsable;
  const callSessionId = voiceCall.sessionId;
  const ownsHandsFreeCall = handsFree && !!hf && hf.active && hf.binding?.sessionId === callSessionId;
  const handsFreeTitle = handsFreeCall?.title ?? null;
  const startVoiceCall = useCallback(() => {
    if (handsFree && hf) {
      hf.startCall({
        sessionId: callSessionId,
        branchId: activeBranchId ?? 'main',
        assetContext: voiceAssetContext,
        scopeEntityId: voiceScopeEntityId,
        title: handsFreeTitle,
      });
      return;
    }
    void voiceCall.start();
  }, [handsFree, hf, callSessionId, activeBranchId, voiceAssetContext, voiceScopeEntityId, handsFreeTitle, voiceCall]);
  // This chat's composer is on screen: the strip steps aside for it.
  const attachComposer = hf?.attachComposer;
  useEffect(() => {
    if (!handsFree || !attachComposer) return;
    return attachComposer(callSessionId);
  }, [handsFree, attachComposer, callSessionId]);
  const ownCallActive = voiceCall.state !== 'idle' && voiceCall.state !== 'error';
  const callActive = ownsHandsFreeCall || ownCallActive;
  // The hands-free call's bar, shown here while the call is for this chat.
  const handsFreeBar =
    ownsHandsFreeCall && hf ? (
      <VoiceCallBar
        state={hf.state}
        status={hf.status}
        userTranscript={hf.userLine}
        assistantStreaming={hf.assistantLine}
        muted={hf.muted}
        onToggleMute={hf.toggleMute}
        onStop={hf.stop}
        onMinimize={handsFreeCall?.onMinimize}
        quietSecondsLeft={hf.quietSecondsLeft}
        onKeepCall={hf.keepCall}
      />
    ) : null;

  // FRONT-2001 round 3 (§7): a GPT-Live-1 delegation handed off to the
  // background can settle on a different pod than this tab is talking to,
  // possibly after the call itself has ended — this is the backstop that
  // notices the delivery (via the background-chat status this same session
  // already polls) and refetches history so the row shows up without a
  // reload.
  useRefreshHistoryWhenBackgroundSettles(voiceCall.sessionId);

  // `handleFiles` is redeclared every render and closes over this render's
  // session and scope, so the handle reads it through a ref rather than
  // pinning whichever copy existed when the handle was built.
  const handleFilesRef = useRef<(files: File[]) => Promise<void>>(async () => {});
  useImperativeHandle(ref, () => ({
    startCall: startVoiceCall,
    addFiles: (files) => {
      void handleFilesRef.current(files);
      requestAnimationFrame(() => focusInput());
    },
  }), [startVoiceCall]);

  // FRONT-531: when opened from the launcher's phone button, start the call
  // once on mount so the user lands straight in the conversation. Guarded so
  // a re-render (or the start() identity changing with call state) can't
  // re-trigger it.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartCall && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startVoiceCall();
    }
  }, [autoStartCall, startVoiceCall]);

  useEffect(() => {
    if (voiceCall.state === 'idle') {
      setCallUserTranscript('');
      setCallAssistantStream('');
    }
  }, [voiceCall.state]);

  useEffect(() => { focusInput(); }, []);

  useEffect(() => {
    if (voice.error) toast.error(`Voice: ${voice.error}`);
  }, [voice.error]);

  // Live timer while recording — declared after `voice` so the state read is
  // safe. Ticks every 250ms, resets when recording stops.
  useEffect(() => {
    if (voice.state !== 'recording') {
      setRecordingMs(0);
      return;
    }
    const startedAt = Date.now();
    setRecordingMs(0);
    const id = window.setInterval(() => setRecordingMs(Date.now() - startedAt), 250);
    return () => window.clearInterval(id);
  }, [voice.state]);

  const slash = useSlashMenu({
    // An empty value never starts with "/", so the menu stays closed.
    value: hideSlashMenu ? '' : text,
    onPick: (cmd) => {
      // Replace the slash token with `/cmd ` and refocus so the user can keep typing args.
      const after = text.includes(' ') ? text.slice(text.indexOf(' ')) : '';
      setText(`/${cmd} ${after.trimStart()}`);
      requestAnimationFrame(() => focusInput());
    },
  });

  async function handleFiles(files: File[], at?: number, bridgeSignal?: AbortSignal) {
    let failed = false;
    if (files.length === 0 || hideAttach) {
      if (bridgeSignal) throw Error('attachments-unavailable');
      return;
    }
    for (const file of files) {
      if (bridgeSignal?.aborted) throw Error('desktop-request-cancelled');
      const localId = crypto.randomUUID();
      setAttachments((xs) => [...xs, { state: 'uploading', localId, name: file.name }]);
      const removePending = () => setAttachments((xs) => xs.filter((a) => a.localId !== localId));
      bridgeSignal?.addEventListener('abort', removePending, { once: true });
      try {
        // FRONT-1841: both optional — undefined on every surface with no
        // chat/scope (the phone composer, the home launcher's pre-session
        // composer), so an upload there is byte-identical to before.
        const row = await upload.mutateAsync({ file, name: file.name, sessionId, scopeEntityId: uploadScopeEntityId });
        if (bridgeSignal?.aborted) throw Error('desktop-request-cancelled');
        setAttachments((xs) =>
          xs.map((a) =>
            a.localId === localId
              ? { state: 'ready', localId, fileId: row.id, name: row.name, contentType: row.contentType, kind: row.kind, source: 'upload' }
              : a,
          ),
        );
      } catch {
        failed = true;
        removePending();
      } finally {
        bridgeSignal?.removeEventListener('abort', removePending);
      }
    }
    if (bridgeSignal && failed) throw Error('upload-failed');
  }

  // The field's keys — Enter sends or queues, Up recalls a queued draft, the
  // slash menu goes first. One handler for the plain textarea and the
  // Markdown field (FRONT-3163), so the two can never disagree about Enter.
  function handleComposerKey(e: Pick<globalThis.KeyboardEvent, 'key' | 'shiftKey' | 'metaKey' | 'ctrlKey' | 'defaultPrevented' | 'preventDefault'>) {
    slash.onKeyDown(e as unknown as KeyboardEvent<HTMLTextAreaElement>);
    if (e.defaultPrevented) return;
    if (e.key === 'ArrowUp' && !text.trim() && queuedDraft) {
      // FRONT-761: recall-to-edit, terminal style — Up in an
      // empty composer pulls the queued message back into the box
      // (and un-queues it). Clicking the chip text does the same.
      e.preventDefault();
      restoreQueuedDraft();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      const trimmed = text.trim();
      if (sendGate ? sendGate(text).disabled : !trimmed) return;
      // FRONT-1182: Enter and Send must not disagree about what
      // "busy" means. Queue only when the queue can actually
      // resolve itself later; if this submit is what unblocks the
      // turn, send it now (decideEnterAction).
      if (
        decideEnterAction({
          turnActive: queueTurnActive,
          submitResolvesTurn: submitResolvesTurn === true,
        }) === 'queue'
      ) {
        // FRONT-761: Enter during ANY in-flight turn — local
        // stream or server-side background continuation —
        // snapshots the draft into the queue and CLEARS the box
        // (like a real send), so the composer stays free for the
        // next thought and the auto-fire can never steal text
        // being typed. Enter again appends to the queued message.
        // Sending never happens here — the queue resolves only on
        // the settled edge (decideQueuedSend); the Send button
        // remains the deliberate escape hatch for a concurrent
        // send during a background run.
        if (hasPending) {
          toast.info('An attachment is still uploading — press Enter again once it is ready.', {
            id: 'chat-queue-uploading',
          });
          return;
        }
        const snap = snapshotBox();
        if (!snap) return;
        // Queued straight away, exactly as before, so the text is
        // persisted and survives an unmount or a slow check.
        setQueuedDraft((prev) => mergeQueuedDraft(prev, snap));
        withdrawIfItAnswersACard(snap);
        return;
      }
      if (hasPending) return;
      submitDraft();
    }
  }

  handleFilesRef.current = handleFiles;

  // FRONT-1567: stage an existing Files-area item (an upload or a saved
  // Knowledge document) picked from the chooser — straight to `ready`, no
  // upload call, since the file already exists. Mirrors `handleFiles`'
  // per-item shape but skips the `uploading` intermediate state entirely.
  function handlePickExisting(option: FilePickerOption) {
    setAttachments((xs) => [
      ...xs,
      { state: 'ready', localId: crypto.randomUUID(), ...pickedFileAttachment(option) },
    ]);
    setFilePickerOpen(false);
    // FRONT-1567 review fix: the picker's trigger is a plain non-interactive
    // `<span>` (see the anchor comment above), so Radix's own close-focus
    // has nothing real to focus and drops it on `<body>` — same pattern as
    // the other post-action refocuses in this component (voice transcript,
    // slash-pick, queued-draft restore).
    requestAnimationFrame(() => focusInput());
  }

  // Paste a screenshot/image straight into the composer. The clipboard->images
  // extraction lives in a shared util (~/lib/clipboard) so other composers can
  // reuse it; here we just preventDefault and hand the files to this composer's
  // upload path.
  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const images = clipboardImageFiles(e);
    if (images.length === 0) return;
    e.preventDefault();
    void handleFiles(images);
  }

  // FRONT-761: snapshot the box into a submit payload and clear it — the one
  // capture path for manual sends and queueing, so a queued draft carries
  // attachments and image mode exactly like a manual send would.
  function snapshotBox(opts?: { allowEmpty?: boolean }): QueuedDraft | null {
    const trimmed = text.trim();
    if (!trimmed && !opts?.allowEmpty) return null;
    const ready = attachments.filter((a): a is Extract<PendingAttach, { state: 'ready' }> => a.state === 'ready');
    const snap = {
      text: trimmed,
      attachments: ready.map(({ fileId, name, contentType, kind, source }) => ({ fileId, name, contentType, kind, source })),
      image: imageModeActive ? { intent: true } : undefined,
    };
    setText('');
    setAttachments([]);
    setImageMode(false);
    return snap;
  }

  // The one submit core — Send button, Enter key, and the queued-send trigger
  // all pass through here. Refuses to run while a local turn is busy; the
  // queued snapshot is deliberately untouched (a manual concurrent send via
  // the Send button must not discard a pending queue).
  // FRONT-1898: the composer used to clear itself and hand the payload to
  // `onSubmit` with no visibility into whether the send actually happened —
  // fine for chat (a send is essentially never flatly rejected; failures
  // happen later, during streaming, after the message is already recorded)
  // but not for a surface like coding sessions, whose POST can be flatly
  // rejected (already in progress, or the backend's run-concurrency limit)
  // before anything is recorded. Awaiting here and restoring on rejection
  // means a caller that opts in (by letting its own rejection propagate)
  // never loses a typed message to a failed send; a caller that doesn't
  // (onSubmit returns void, or a promise that never rejects) is unaffected.
  async function submitPayload(payload: QueuedDraft, meta?: { queued?: boolean }) {
    if (isBusy) return;
    // FRONT-1898 (code-review finding): onSend is deliberately OUTSIDE the
    // try — a caller's onSend throwing after onSubmit already succeeded must
    // not be misread as a failed send. That would restore text/attachments
    // for a message that, in fact, already went out, showing the user a
    // confusing duplicate of something already sent.
    try {
      await onSubmit(payload.text, payload.attachments, payload.image, meta);
    } catch {
      restoreSnapshotIntoBox(payload);
      return;
    }
    onSend?.();
  }

  const desktopActions = useRef({ text, isBusy, budgetBlocked, attachments, onSubmit });
  const desktopCommit = useRef<{ text: string; resolve: () => void; reject: (error: Error) => void; signal: AbortSignal; abort: () => void } | null>(null);
  useLayoutEffect(() => {
    desktopActions.current = { text, isBusy, budgetBlocked, attachments, onSubmit };
    const pending = desktopCommit.current;
    if (pending && text === pending.text && inputRef.current?.value === text) {
      pending.signal.removeEventListener('abort', pending.abort);
      desktopCommit.current = null;
      pending.resolve();
    }
  });
  useEffect(() => {
    if (!desktopComposer || !sessionId || !fileInputRef.current) return;
    const unregister = registerDesktopComposer(sessionId, {
      input: fileInputRef.current,
      draft: (value, signal) => new Promise<void>((resolve, reject) => {
        if (signal.aborted) { reject(Error('desktop-request-cancelled')); return; }
        const abort = () => { if (desktopCommit.current?.signal === signal) desktopCommit.current = null; reject(Error('desktop-request-cancelled')); };
        signal.addEventListener('abort', abort, { once: true });
        desktopCommit.current = { text: value, resolve, reject, signal, abort };
        setText(value);
        // Identical values can skip a React render; the currently committed
        // controlled input still proves the draft is already present.
        if (desktopActions.current.text === value && inputRef.current?.value === value) {
          signal.removeEventListener('abort', abort); desktopCommit.current = null; resolve();
        }
      }),
      send: async (signal) => {
        const state = desktopActions.current;
        if (signal.aborted || state.isBusy || state.budgetBlocked || state.attachments.length || !state.text.trim()) throw Error('composer-cannot-send');
        await state.onSubmit(state.text.trim(), [], undefined, { desktopSignal: signal });
        if (signal.aborted) return;
        setText(prev => prev === state.text ? '' : prev);
        onSend?.();
      },
    });
    return () => {
      unregister();
      const pending = desktopCommit.current;
      if (pending) {
        pending.signal.removeEventListener('abort', pending.abort);
        pending.reject(Error('composer-unmounted'));
        desktopCommit.current = null;
      }
    };
  }, [desktopComposer, sessionId]);

  function submitDraft() {
    if (isBusy || attachments.some((a) => a.state === 'uploading')) return;
    if (sendGate?.(text).disabled) return;
    const snap = snapshotBox({ allowEmpty: !!sendGate });
    if (!snap) return;
    void submitPayload(snap);
  }

  // Put an arbitrary captured snapshot back into the composer. Prepends to
  // whatever the user has typed since; ready attachments and image mode are
  // restored too. Shared by `restoreQueuedDraft` (cancel X, a held queue on
  // manual Stop / errored turn) and, per FRONT-1898, a rejected DIRECT
  // submit — either way, a payload this component already captured is never
  // silently dropped.
  function restoreSnapshotIntoBox(snap: QueuedDraft) {
    setText((prev) => (prev.trim() ? `${snap.text}\n\n${prev}` : snap.text));
    setAttachments((prev) => [
      ...prev,
      ...snap.attachments.map(
        (a): Extract<PendingAttach, { state: 'ready' }> => ({
          state: 'ready',
          // FRONT-1567: prefixed with source, not just fileId — an upload id
          // and a document filename can otherwise collide on this key.
          localId: `restored-${a.source ?? 'upload'}-${a.fileId}`,
          fileId: a.fileId,
          name: a.name,
          contentType: a.contentType,
          // ChatAttachment.kind is a plain string at the submit boundary; the
          // snapshot only ever holds values that came out of a ready
          // PendingAttach, so narrowing back is safe.
          kind: a.kind as Extract<PendingAttach, { state: 'ready' }>['kind'],
          // Absent means "upload" — a draft queued before this ticket never
          // had a source field at all (see ChatAttachment's docblock).
          source: a.source ?? 'upload',
        }),
      ),
    ]);
    if (snap.image?.intent) setImageMode(true);
  }

  // FRONT-2555: a plain "yes"/"no" queued mid-turn may answer the approval
  // card the turn is waiting on — then it is taken back out of the queue.
  // Anything else (or a failed check) stays queued.
  function withdrawIfItAnswersACard(snap: QueuedDraft) {
    if (!onAnswerWhileBusy || snap.attachments.length > 0 || snap.image) return;
    void onAnswerWhileBusy(snap.text).then((answered) => {
      if (answered) setQueuedDraft((prev) => withoutQueuedText(prev, snap.text));
    });
  }

  function restoreQueuedDraft(reason?: string) {
    const snap = queuedDraft;
    if (!snap) return;
    setQueuedDraft(null);
    restoreSnapshotIntoBox(snap);
    if (reason) toast.info(reason, { id: 'chat-queue-restored' });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    submitDraft();
  }

  // FRONT-1163 (pr-panel finding): the same chat open in two tabs shares one
  // localStorage key — both can independently decide 'fire' on the same
  // restored draft with no coordination. Re-checking storage right before
  // actually sending closes the common case: whichever tab's effect runs
  // first clears the key when it fires, so the second tab's re-check sees
  // it already gone and skips sending a second copy of the same message.
  function fireQueuedDraft(snap: QueuedDraft) {
    if (!loadQueuedDraft(queuedKey)) return;
    void submitPayload(snap, { queued: true });
  }

  // FRONT-761: resolve the queue exactly on the busy -> idle edge. Deps are
  // deliberately narrow (the busy flip is the only trigger); the closure is
  // fresh from the render that flipped it, so text/queued/attachments are
  // current. Decision semantics live in queued-send.ts (unit-tested).
  useEffect(() => {
    const wasBusy = prevBusyRef.current;
    prevBusyRef.current = queueTurnActive;
    const stopRequested = stopRequestedRef.current;
    if (wasBusy && !queueTurnActive) {
      // The stop flag describes THIS turn's ending only. Clear it on every
      // busy -> idle edge — even when nothing is queued — or a Stop on a
      // queue-less turn leaks into the next queued completion and wrongly
      // holds it. (Aikido finding on PR #453.)
      stopRequestedRef.current = false;
    }
    const decision = decideQueuedSend({
      wasBusy,
      isBusy: queueTurnActive,
      queued,
      stopRequested,
      turnError: turnError === true,
      hasText: (queuedDraft?.text.trim().length ?? 0) > 0,
    });
    if (decision === 'none') return;
    if (decision === 'cancel') {
      setQueuedDraft(null);
    } else if (decision === 'fire') {
      const snap = queuedDraft;
      setQueuedDraft(null);
      if (snap) fireQueuedDraft(snap);
    } else if (decision === 'hold') {
      // Manual Stop or an errored turn: never auto-send into it — and never
      // strand the draft either. It goes back into the composer.
      restoreQueuedDraft('The reply was interrupted — your queued draft is back in the composer.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueTurnActive]);

  // FRONT-1163: keep the persisted snapshot in sync so a remount can restore
  // it (see the queuedDraft init above). Clearing falls out for free — every
  // path that resolves the queue (fire/hold/cancel) already sets state back
  // to null, which persistQueuedDraft treats as "remove."
  // FRONT-1182 (review): depend on the key this effect actually READS. It used
  // to track `sessionId`, which it no longer reads at all — so a key that
  // changed without a remount would neither write under the new key nor clear
  // the old one. Masked today only because the coding modal cycles `session`
  // through null on a project switch and unmounts this composer, which is
  // correctness by accident rather than by design.
  useEffect(() => {
    persistQueuedDraft(queuedKey, queuedDraft);
  }, [queuedKey, queuedDraft]);

  // BUG-055 / FRONT-1163: a draft restored above never gets a busy->idle EDGE to
  // resolve on if the turn already finished while this chat was unmounted —
  // the effect above only fires on a transition it observes itself. Reconcile
  // it once, here, against the real turn status instead. `reconciledRef`
  // means this only ever runs its verdict once per mount: while queuePending
  // is true we keep re-checking (not yet marked reconciled) until the caller
  // actually knows the turn status, then decide exactly once. A still-running
  // turn is left alone — the effect above owns resolving it when it settles.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (reconciledRef.current || queuePending) return;
    reconciledRef.current = true;
    const decision = decideQueuedRestore({
      hasQueuedDraft: queued,
      pending: false,
      turnActive: queueTurnActive,
      turnError: turnError === true,
    });
    if (decision === 'fire') {
      const snap = queuedDraft;
      setQueuedDraft(null);
      if (snap) fireQueuedDraft(snap);
    } else if (decision === 'hold') {
      restoreQueuedDraft('The reply finished while you were away, but it looked like an error — your queued draft is back in the composer.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuePending, queueTurnActive, turnError, queued]);

  const showStop = isStreaming ?? isBusy;
  const showWaiting = isBusy && !showStop;
  const recording = voice.state === 'recording';
  const transcribing = voice.state === 'transcribing';
  const hasPending = attachments.some((a) => a.state === 'uploading');

  // Border-top + background are owned by the parent so they can bleed to the
  // viewport edge when the page wraps us in a `.bleed-x`. The form itself
  // stays inside whatever container the page provides.
  // FRONT-1194: a spent compute budget means the next turn is refused before it
  // starts, so swap the composer for an explanation rather than accepting input
  // that cannot go anywhere.
  if (budgetBlocked) {
    return (
      <div className="px-5 py-3">
        <BudgetComposerLock />
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="px-5 py-3">
      {imageModeActive && (
        <div className="mb-2 flex items-center gap-2 text-[length:var(--text-2xs)] text-muted-foreground">
          <ImageIcon size={11} />
          <span>Generating with GPT Image 2</span>
          <RegionBadge region="eu" />
        </div>
      )}
      {callActive ? (
        handsFreeBar ?? (
          <VoiceCallBar
            state={voiceCall.state}
            status={voiceCall.status}
            userTranscript={callUserTranscript}
            assistantStreaming={callAssistantStream}
            muted={voiceCall.muted}
            onToggleMute={voiceCall.toggleMute}
            onStop={voiceCall.stop}
          />
        )
      ) : recording ? (
        <RecordingBar
          elapsedMs={recordingMs}
          onCancel={voice.cancel}
          onFinish={voice.stopAndTranscribe}
        />
      ) : (
        <>
          {transcribing && (
            <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              <CircleNotchIcon size={12} className="animate-spin" />
              Transcribing…
            </div>
          )}
          {taggedFiles.length > 0 && (
            <div data-testid="composer-legacy-files" className="mb-2 flex flex-wrap gap-2">
              {taggedFiles.map((a) => (
                <span key={a.localId} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs">
                  {a.state === 'uploading' ? <CircleNotchIcon size={12} className="animate-spin" /> : null}
                  {a.name}
                  <button
                    type="button"
                    className="opacity-60 hover:opacity-100"
                    onClick={() => setAttachments((xs) => xs.filter((x) => x.localId !== a.localId))}
                    aria-label="Remove"
                  >
                    <XIcon size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {queuedDraft && (
            <div className="mb-2 flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs text-foreground animate-in fade-in slide-in-from-bottom-1 duration-200">
              <CircleNotchIcon size={12} className="animate-spin text-primary shrink-0" />
              <Tip label="Click to edit — returns the queued message to the composer (or press Up in the empty composer)">
                <button
                  type="button"
                  className="min-w-0 truncate text-left"
                  onClick={() => restoreQueuedDraft()}
                >
                  Queued — sends when the reply finishes:{' '}
                  <span className="text-muted-foreground">{queuedDraft.text}</span>
                </button>
              </Tip>
              <button
                type="button"
                className="ml-auto shrink-0 relative rounded-sm p-1 opacity-60 hover:opacity-100 transition-[opacity,scale] active:scale-[0.96] after:absolute after:-inset-2.5 after:content-['']"
                onClick={() => restoreQueuedDraft()}
                aria-label="Cancel queued send and restore the draft to the composer"
              >
                <XIcon size={12} />
              </button>
            </div>
          )}
          <div
            onDragOver={(e) => { e.preventDefault(); if (!hideAttach) setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); void handleFiles(Array.from(e.dataTransfer.files)); }}
            data-asking={topSlot ? 'true' : undefined}
            className={cn('relative rounded-xl border border-border bg-card focus-within:border-foreground/40 transition-colors shadow-sm', isDragging && 'ring-2 ring-primary')}
          >
            {slash.menu}
            {topSlot?.({
              text,
              focusSend: () => {
                const btn = sendButtonRef.current;
                if (btn && !btn.disabled) btn.focus();
                else focusInput();
              },
              // Presses Send for the slot (the Builder's Done starts a new
              // project) — the same gates as a click, since it IS one.
              send: () => {
                const btn = sendButtonRef.current;
                if (btn && !btn.disabled) btn.click();
                else focusInput();
              },
            })}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const input = e.currentTarget;
                const files = Array.from(input.files ?? []);
                void (desktopComposer && sessionId
                  ? desktopComposerFiles(sessionId, input, files.length, signal => handleFiles(files, undefined, signal))
                  : handleFiles(files));
              }}
            />
            {/*
              FRONT-246: the composer stays editable while the agent is working
              so the user can draft a follow-up. Sending mid-stream is still
              blocked — the Enter handler keeps its `isBusy` guard and the Send
              button is swapped for Stop while streaming — so this unlocks typing
              only, it does not start a second turn. Disabled only while
              transcribing, when speech-to-text owns the field.
            */}
            {/* One text line holds the attached files AND the typed text:
                each file is a tag sitting in the text, ahead of the words,
                never a row of its own above them (eddy, 2026-10-26). The
                line wraps like text; a click on its empty part lands in the
                field. */}
            <div data-testid="composer-text-line" className="contents">
              <Textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => handleComposerKey(e)}
                  placeholder={
                    transcribing
                      ? 'Transcribing…'
                      : showWaiting
                        ? 'Working…'
                        : placeholder ?? `Ask ${displayName} anything…`
                  }
                  disabled={transcribing}
                  rows={1}
                  className="resize-none min-h-[76px] max-h-[200px] leading-snug px-4 pt-3 pb-2 text-[length:var(--text-base)] border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-0"
                />
            </div>

            {/*
              FRONT-1180: this toolbar used to be two `absolute`-positioned
              overlays floating on top of the textarea, kept clear of typed
              text only by a `pb-10` bottom-padding reserve inside the
              textarea that happened to match the overlay's height — no
              structural relationship between the two. The textarea also ran
              its own imperative resize effect (removed above) alongside the
              native `field-sizing-content` on the shared Textarea primitive,
              and the two disagreeing about the box's real height is what let
              the coincidental padding drift out of alignment with what was
              actually rendered. Now this toolbar is a normal flow sibling
              below the textarea, so it reserves real layout space that can't
              silently drift apart from the text again.
            */}
            <div className="flex items-center gap-1.5 px-2.5 pb-2">
              {!hideModelSwitch && modelPickerEnabled && (
                <ComposerModelPicker sessionId={sessionId} disabled={isBusy} />
              )}
              {!hideModelSwitch && !modelPickerEnabled && (
                <div className="flex items-center gap-1.5">
                  <Tip
                    label={
                      modelMode === 'fast'
                        ? 'Fast model active — tap to switch back to your normal chat model'
                        : 'Switch to your Fast model for the next messages (set it in Settings → Models)'
                    }
                  >
                    <button
                      type="button"
                      onClick={() => pickModelMode('fast')}
                      disabled={isBusy || modelModePending}
                      aria-pressed={modelMode === 'fast'}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 disabled:opacity-50',
                        modelMode === 'fast'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50',
                      )}
                    >
                      <LightningIcon size={13} weight={modelMode === 'fast' ? 'fill' : 'regular'} />
                      Fast
                    </button>
                  </Tip>
                  <Tip
                    label={
                      modelMode === 'thoughtful'
                        ? 'Thoughtful model active — tap to switch back to your normal chat model'
                        : 'Switch to your Thoughtful model for the next messages (set it in Settings → Models)'
                    }
                  >
                    <button
                      type="button"
                      onClick={() => pickModelMode('thoughtful')}
                      disabled={isBusy || modelModePending}
                      aria-pressed={modelMode === 'thoughtful'}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 disabled:opacity-50',
                        modelMode === 'thoughtful'
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50',
                      )}
                    >
                      <BrainIcon size={13} weight={modelMode === 'thoughtful' ? 'fill' : 'regular'} />
                      Thoughtful
                    </button>
                  </Tip>
                </div>
              )}

              <div className="ml-auto flex items-center gap-1.5">
                {/*
                  FRONT-1567: the picker's popover is anchored to a hidden
                  span that fills this `relative` wrapper — sized to sit
                  exactly under the paperclip button — rather than being a
                  menu item's own trigger. Nesting a Popover inside a
                  DropdownMenu item is a known trap in this repo (see the
                  Tip/asChild note in CLAUDE.md); opening/closing goes purely
                  through `filePickerOpen` state, set once the attach menu has
                  genuinely closed (see its `onCloseAutoFocus` below) rather
                  than directly from the menu item's `onSelect`.
                */}
                {!hideAttach && (
                <div className="relative">
                  <FilePicker
                    open={filePickerOpen}
                    onOpenChange={setFilePickerOpen}
                    trigger={<span aria-hidden="true" className="absolute inset-0 pointer-events-none" />}
                    onSelect={handlePickExisting}
                    excludeKeys={attachments
                      .filter((a): a is Extract<PendingAttach, { state: 'ready' }> => a.state === 'ready')
                      .map((a) => `${a.source === 'document' ? 'doc' : 'upload'}:${a.fileId}`)}
                  />
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        disabled={isBusy}
                        tooltip="Attach files"
                        aria-label="Attach files"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      >
                        <PaperclipIcon size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="w-52"
                      onCloseAutoFocus={(e) => {
                        // FRONT-1567 review fix (round 2): two compounding
                        // mechanisms were fighting the freshly-mounted
                        // Knowledge popover, and both need to stay fixed —
                        // don't delete either half as "redundant" cleanup.
                        //
                        // (1) DropdownMenu is `modal` BY DEFAULT, so for the
                        // ~100ms this menu plays its own exit animation
                        // (Presence keeps it mounted until the CSS animation
                        // ends), its FocusScope's `trapped` focus guard and
                        // its DismissableLayer's `disableOutsidePointerEvents`
                        // stay ACTIVE — fighting any focus/pointer landing on
                        // the popover that mounted in the same tick. `modal=
                        // {false}` above turns both off for the whole menu,
                        // which is what actually removes the fight; without
                        // it, suppressing autofocus below only stops the
                        // instant flash, not this ~200ms race.
                        //
                        // (2) Independently of modal-ness, FocusScope always
                        // schedules an unmount autofocus in a setTimeout(0)
                        // once it actually unmounts, and by default that
                        // refocuses the paperclip trigger — which the
                        // popover's own dismissable layer would read as a
                        // focus-outside click and close itself over.
                        // Suppressing that here is still load-bearing even
                        // with modal={false}.
                        e.preventDefault();
                        // Fires the action either menu item below staged —
                        // AFTER the menu has genuinely finished closing (this
                        // callback only runs once FocusScope actually
                        // unmounts), never synchronously from onSelect. One
                        // more rAF frame lets that unmount's own cleanup
                        // (removing the old DismissableLayer's listeners)
                        // fully settle before either the native file dialog
                        // opens or the popover's Command input mounts and
                        // autofocuses — same margin for both actions so they
                        // share one lifecycle instead of two divergent hacks.
                        const action = pendingAttachActionRef.current;
                        pendingAttachActionRef.current = null;
                        if (!action) return;
                        requestAnimationFrame(() => {
                          if (action === 'upload') {
                            fileInputRef.current?.click();
                          } else {
                            setFilePickerOpen(true);
                          }
                        });
                      }}
                    >
                      <DropdownMenuItem
                        onSelect={() => {
                          // FRONT-1567 review fix: don't click the hidden
                          // file input directly from onSelect — a
                          // programmatic `.click()` fired while this menu is
                          // still unmounting can fall foul of transient-
                          // activation rules and silently no-op. Defer to
                          // onCloseAutoFocus above, which fires once the
                          // menu has genuinely finished closing.
                          pendingAttachActionRef.current = 'upload';
                        }}
                      >
                        <UploadSimpleIcon size={14} /> Upload from device
                      </DropdownMenuItem>
                      {/* FRONT-1567: "Knowledge" matches the sidebar/page
                          label FRONT-1460 renamed the Files area to — see
                          sidebar-nav-catalog.ts's `files` entry. */}
                      <DropdownMenuItem
                        onSelect={() => {
                          // FRONT-1567 review fix (round 2): don't open the
                          // popover synchronously here — same reasoning and
                          // same deferred mechanism as "Upload from device"
                          // above (see onCloseAutoFocus).
                          pendingAttachActionRef.current = 'browse';
                        }}
                      >
                        <FilesIcon size={14} /> Choose from Knowledge
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                )}
                {hasImageModels && (
                  <Button
                    type="button"
                    onClick={() => setImageMode((v) => !v)}
                    variant="ghost"
                    size="icon-sm"
                    disabled={isBusy}
                    tooltip={
                      imageMode
                        ? 'Image mode on — your next message generates an image'
                        : 'Generate an image from your next message'
                    }
                    aria-label="Generate an image"
                    aria-pressed={imageMode}
                    className={cn(
                      'h-8 w-8 transition-transform active:scale-95',
                      imageMode
                        ? 'text-primary hover:text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <ImageIcon size={16} weight={imageMode ? 'fill' : 'regular'} />
                  </Button>
                )}
                {!hideLiveCall && (
                  <Button
                    type="button"
                    onClick={onStartCall ?? startVoiceCall}
                    variant="ghost"
                    size="icon-sm"
                    disabled={isBusy || transcribing}
                    tooltip="Start a live voice call with EDDY (hands-free)"
                    aria-label="Start a voice call"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  >
                    <PhoneIcon size={16} weight="regular" />
                  </Button>
                )}
                {!hideDictation && (
                  <Button
                    type="button"
                    onClick={voice.start}
                    variant="ghost"
                    size="icon-sm"
                    disabled={isBusy || transcribing}
                    tooltip="Record voice → tap stop to transcribe → edit before sending"
                    aria-label="Record voice"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  >
                    {transcribing ? (
                      <CircleNotchIcon size={16} className="animate-spin" />
                    ) : (
                      <MicrophoneIcon size={16} weight="regular" />
                    )}
                  </Button>
                )}
                {/* FRONT-3211: distinct keys, so Stop, Working and Send are
                    never the SAME button element. Stop's click frees the
                    composer and, with a queued draft, puts it back in the box,
                    all inside the click; reused, the element is a submit
                    button by the time the click's default action runs, and
                    that Stop sent the draft it had just put back. */}
                {showStop ? (
                  <Button
                    key="stop"
                    type="button"
                    onClick={() => {
                      // FRONT-761: a manual Stop holds a draft that was ALREADY
                      // queued (the user interrupted the turn; don't auto-send
                      // into it). A draft queued AFTER pressing Stop still fires
                      // — that intent was expressed after the interruption
                      // decision. The flag is armed only when a queue exists so
                      // it can't leak state across queue-less turns.
                      if (queued) stopRequestedRef.current = true;
                      onStop();
                    }}
                    variant="destructive"
                    size="sm"
                    className="h-8 gap-1.5"
                  >
                    <StopIcon size={14} weight="fill" /> Stop
                  </Button>
                ) : showWaiting ? (
                  <Button key="working" type="button" disabled size="sm" className="h-8 gap-1.5">
                    <CircleNotchIcon size={14} className="animate-spin" /> Working
                  </Button>
                ) : (
                  <Button
                    key="send"
                    type="submit"
                    ref={sendButtonRef}
                    disabled={(sendGate ? sendGate(text).disabled : !text.trim()) || transcribing || hasPending}
                    size="sm"
                    className="h-8 gap-1.5 bg-foreground text-background hover:bg-foreground/90"
                  >
                    {submitLabel ?? 'Send'} <ArrowUpIcon size={13} weight="bold" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </form>
  );
});

/**
 * Dedicated bar shown in place of the composer while voice recording is
 * active. Replaces the previous three-button cluster (cancel / stop / send)
 * with one focused affordance: live waveform-style indicator + timer on the
 * left, a single primary "Done" button on the right, and a quiet trash
 * cancel inline. Send is intentionally hidden — there's nothing to send
 * yet, and showing it during recording was confusing per the screenshot.
 *
 * Semantics:
 *  - "Done" = success path (stop + transcribe). Filled primary button.
 *  - "Cancel" = destructive (throws audio away). Ghost+trash, secondary
 *    weight, requires no confirm because the audio is ephemeral and easy to
 *    redo.
 *
 * Exported (FRONT-1566) so `chat-role-dialog.tsx`'s own dictation control can
 * reuse it verbatim instead of cloning the markup — same recording-affordance
 * precedent, different host surface.
 */
export function RecordingBar({
  elapsedMs,
  onCancel,
  onFinish,
}: {
  elapsedMs: number;
  onCancel: () => void;
  onFinish: () => void;
}) {
  const seconds = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(seconds / 60).toString();
  const ss = (seconds % 60).toString().padStart(2, '0');
  return (
    <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5 min-h-[76px]">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-60" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
      </span>
      <MicrophoneIcon size={16} className="text-destructive shrink-0" weight="fill" />
      <div className="flex-1 flex items-baseline gap-2 min-w-0">
        <span className="text-sm font-medium text-foreground">Recording</span>
        <span className="text-xs text-muted-foreground tabular-nums">{mm}:{ss}</span>
      </div>
      <Button
        type="button"
        onClick={onCancel}
        variant="ghost"
        size="sm"
        className="text-muted-foreground hover:text-destructive"
        tooltip="Discard recording"
      >
        <XIcon size={14} /> Cancel
      </Button>
      <Button
        type="button"
        onClick={onFinish}
        size="sm"
        className="gap-1.5"
        tooltip="Stop recording &amp; transcribe"
      >
        <StopIcon size={14} weight="fill" /> Done
      </Button>
    </div>
  );
}
